/**
 * Admin Loom Settings — Oct 2026
 * Lets admins inspect and override per-project generation config
 * (model, structure, chapter count, target words, caching) for any
 * novel framework without impersonating the owner.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STRUCTURES = [
  { value: '3-act', label: '3-Act (Classical)' },
  { value: '5-act', label: '5-Act (Freytag)' },
  { value: '7-act', label: '7-Act (Dan Wells)' },
];

const MODELS = [
  'claude-sonnet-5-5-20251016',
  'claude-opus-4-8-20250514',
  'claude-haiku-5-5-20251016',
];

export default function LoomSettingsPanel() {
  const [frameworks, setFrameworks] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [editing,    setEditing]    = useState(null);   // { fid, field }
  const [busy,       setBusy]       = useState({});     // fid → bool
  const [draftPatch, setDraftPatch] = useState({});     // fid → AdminLoomPatch

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/loom/frameworks`);
      setFrameworks(r.data || []);
    } catch {
      toast.error('Could not load frameworks.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (fid) => {
    const fw = frameworks.find((f) => f.id === fid) || {};
    setDraftPatch((p) => ({
      ...p,
      [fid]: {
        model_id:       fw.model_id       || '',
        structure:      fw.structure      || '3-act',
        chapter_count:  fw.chapter_count  || 20,
        target_words:   fw.target_words   || 80000,
        prompt_caching: fw.prompt_caching ?? true,
      },
    }));
    setEditing(fid);
  };

  const cancelEdit = () => setEditing(null);

  const saveEdit = async (fid) => {
    const patch = draftPatch[fid] || {};
    setBusy((b) => ({ ...b, [fid]: true }));
    try {
      await axios.patch(`${API}/api/admin/loom/frameworks/${fid}`, patch);
      toast.success('Framework config updated.');
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setBusy((b) => ({ ...b, [fid]: false }));
    }
  };

  const setPatch = (fid, field, value) =>
    setDraftPatch((p) => ({ ...p, [fid]: { ...(p[fid] || {}), [field]: value } }));

  const filtered = frameworks.filter((f) => {
    const q = search.toLowerCase();
    return !q || (f.title || '').toLowerCase().includes(q) || (f.user_id || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold text-gray-900">Loom Framework Settings</h2>
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
          {frameworks.length} frameworks
        </span>
        <button
          onClick={load}
          className="ml-auto rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
          data-testid="loom-refresh-btn"
        >
          Refresh
        </button>
      </div>

      <input
        type="search"
        placeholder="Filter by title or user…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full max-w-sm rounded-md border border-gray-200 px-3 py-1.5 text-sm focus:border-rose-400 focus:outline-none"
        data-testid="loom-search"
      />

      {loading && <div className="text-sm text-gray-400">Loading…</div>}

      <div className="overflow-x-auto rounded-lg border border-gray-100">
        <table className="min-w-full text-sm" data-testid="loom-table">
          <thead>
            <tr className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <th className="px-4 py-2 text-left">Title</th>
              <th className="px-4 py-2 text-left">User</th>
              <th className="px-4 py-2 text-left">Model</th>
              <th className="px-4 py-2 text-center">Structure</th>
              <th className="px-4 py-2 text-center">Chapters</th>
              <th className="px-4 py-2 text-center">Words</th>
              <th className="px-4 py-2 text-center">Cache</th>
              <th className="px-4 py-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map((fw) => {
              const isEditing = editing === fw.id;
              const patch     = draftPatch[fw.id] || {};
              return (
                <tr key={fw.id} className={isEditing ? 'bg-rose-50' : 'hover:bg-gray-50'}>
                  <td className="max-w-[200px] truncate px-4 py-2 font-medium text-gray-900">
                    {fw.title || <span className="text-gray-400 italic">Untitled</span>}
                  </td>
                  <td className="max-w-[120px] truncate px-4 py-2 text-xs text-gray-500">
                    {fw.user_id || '—'}
                  </td>

                  {/* Model */}
                  <td className="px-4 py-2">
                    {isEditing ? (
                      <select
                        value={patch.model_id || ''}
                        onChange={(e) => setPatch(fw.id, 'model_id', e.target.value)}
                        className="rounded border border-gray-200 px-2 py-0.5 text-xs"
                        data-testid={`loom-model-${fw.id}`}
                      >
                        <option value="">— default —</option>
                        {MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    ) : (
                      <span className="font-mono text-xs text-gray-600">{fw.model_id || '—'}</span>
                    )}
                  </td>

                  {/* Structure */}
                  <td className="px-4 py-2 text-center">
                    {isEditing ? (
                      <select
                        value={patch.structure || '3-act'}
                        onChange={(e) => setPatch(fw.id, 'structure', e.target.value)}
                        className="rounded border border-gray-200 px-2 py-0.5 text-xs"
                        data-testid={`loom-structure-${fw.id}`}
                      >
                        {STRUCTURES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    ) : (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs">
                        {fw.structure || '3-act'}
                      </span>
                    )}
                  </td>

                  {/* Chapter count */}
                  <td className="px-4 py-2 text-center">
                    {isEditing ? (
                      <input
                        type="number" min={5} max={60}
                        value={patch.chapter_count || 20}
                        onChange={(e) => setPatch(fw.id, 'chapter_count', Number(e.target.value))}
                        className="w-16 rounded border border-gray-200 px-2 py-0.5 text-center text-xs"
                        data-testid={`loom-chapters-${fw.id}`}
                      />
                    ) : (
                      fw.chapter_count || '—'
                    )}
                  </td>

                  {/* Target words */}
                  <td className="px-4 py-2 text-center">
                    {isEditing ? (
                      <input
                        type="number" min={10000} max={250000} step={1000}
                        value={patch.target_words || 80000}
                        onChange={(e) => setPatch(fw.id, 'target_words', Number(e.target.value))}
                        className="w-24 rounded border border-gray-200 px-2 py-0.5 text-center text-xs"
                        data-testid={`loom-words-${fw.id}`}
                      />
                    ) : (
                      (fw.target_words || 0).toLocaleString()
                    )}
                  </td>

                  {/* Caching */}
                  <td className="px-4 py-2 text-center">
                    {isEditing ? (
                      <button
                        type="button"
                        onClick={() => setPatch(fw.id, 'prompt_caching', !patch.prompt_caching)}
                        className="relative inline-flex h-5 w-9 rounded-full transition-colors"
                        style={{ backgroundColor: patch.prompt_caching ? '#8A4C3F' : '#9ca3af' }}
                        data-testid={`loom-caching-${fw.id}`}
                      >
                        <span
                          className="inline-block h-4 w-4 rounded-full bg-white shadow transition-transform mt-0.5"
                          style={{ transform: patch.prompt_caching ? 'translateX(1.125rem)' : 'translateX(0.125rem)' }}
                        />
                      </button>
                    ) : (
                      <span className={`text-xs font-semibold ${(fw.prompt_caching ?? true) ? 'text-emerald-600' : 'text-gray-400'}`}>
                        {(fw.prompt_caching ?? true) ? 'On' : 'Off'}
                      </span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-2 text-center">
                    {isEditing ? (
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => saveEdit(fw.id)}
                          disabled={busy[fw.id]}
                          className="rounded-sm bg-rose-700 px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50"
                          data-testid={`loom-save-${fw.id}`}
                        >
                          {busy[fw.id] ? '…' : 'Save'}
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="rounded-sm border border-gray-200 px-2.5 py-1 text-xs text-gray-600"
                          data-testid={`loom-cancel-${fw.id}`}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => startEdit(fw.id)}
                        className="rounded-sm border border-gray-200 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50"
                        data-testid={`loom-edit-${fw.id}`}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-400">
                  {search ? 'No frameworks match your search.' : 'No frameworks found.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
